import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { AppDispatch } from 'src/infra/stores/store';
import { logoutActionCreator } from 'src/infra/stores/reducer/auth';

import IconArrowDown from 'src/assets/images/icons/arrow-down.svg';

import IconLogout from 'src/assets/images/icons/logout-new.svg';
import IconSort from 'src/assets/images/icons/sort-grey.svg';
import { clearSelectedBilledEntities, selectSelectedBilledEntities } from 'src/infra/stores/reducer/billedEntitiesSlice';
import { beautifyEnum } from 'src/utils/utility';
import { BilledEntitiesMenu } from './BilledEntitiesMenu';
import { ApiVersionSwitch } from './ApiVersionSwitch';
import { DeleteModeSwitch } from './DeleteModeSwitch';

export const AccountMenu = ({ id }: { id?: string }) => {
  const dispatch = useDispatch<AppDispatch>();
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const [isShowAccountMenu, setIsShowAccountMenu] = useState(false);
  const [isShowBilledMenu, setIsShowBilledMenu] = useState(false);
  const savedSelectedEntities = useSelector(selectSelectedBilledEntities);

  // Reference to toggle button
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    /**
     * Close menu when clicked outside of menu element
     */
    function handleClickOutside(event) {
      const ddMenu = document.getElementById('menu-');
      const triggerMenu = document.getElementById('btn-trigger-notif');
      const inDdMenu = ddMenu && ddMenu.contains(event.target);
      const inTriggerMenu = triggerMenu && triggerMenu.contains(event.target);
      const isToggleButton = toggleButtonRef.current && toggleButtonRef.current.contains(event.target);

      // Don't close if clicking the toggle button - let the button's onClick handle it
      if (!isToggleButton && accountMenuRef.current && !accountMenuRef.current.contains(event.target) && !inDdMenu && !inTriggerMenu) {
        setIsShowAccountMenu(false);
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [accountMenuRef, setIsShowAccountMenu]);

  // Function to toggle billed menu visibility - opens BilledEntitiesMenu
  const toggleBilledMenu = () => {
    // Don't toggle the menu if it's already open
    if (!isShowBilledMenu) {
      // Keep the arrow rotated by ensuring one menu is always visible
      // First open the billed menu, then close the account menu to prevent flashing
      setIsShowBilledMenu(true);
      // Small delay before closing account menu to ensure smooth transition
      setTimeout(() => setIsShowAccountMenu(false), 50);
    }
  };

  // Function to switch from BilledEntitiesMenu back to AccountMenu
  const switchToAccountMenu = () => {
    // Keep the arrow rotated by ensuring one menu is always visible
    // First open the account menu, then close the billed menu to prevent flashing
    setIsShowAccountMenu(true);
    // Small delay before closing billed menu to ensure smooth transition
    setTimeout(() => setIsShowBilledMenu(false), 50);
  };

  // Handler for closing the billed menu
  const closeBilledMenu = () => {
    setIsShowBilledMenu(false);
  };

  return (
    <div id={id} className='relative'>
      <button
        type='button'
        ref={toggleButtonRef}
        aria-label='account-menu'
        className='relative w-30 h-30 rounded-4 bg-[#373E43] flex items-center justify-center'
        onClick={() => {
          // Don't allow opening account menu if billed menu is visible
          if (!isShowBilledMenu) {
            setIsShowAccountMenu(!isShowAccountMenu);
          }
        }}
      >
        <img src={IconArrowDown} alt='' className={`transition-transform duration-300 ${isShowAccountMenu || isShowBilledMenu ? 'rotate-180' : 'rotate-0'}`} />
      </button>

      {/* Account Menu with slide animation */}
      <div
        ref={accountMenuRef}
        className={`absolute top-[64px] right-0 w-[260px] bg-white text-blue_grey-90 border border-[#DFDFDF] rounded-8 z-[30] flex flex-col max-h-[80vh] shadow-custom transform transition-all duration-300 ease-in-out ${
          isShowAccountMenu && !isShowBilledMenu ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20 pointer-events-none'
        }`}
      >
        <div className='flex flex-col gap-12 p-8'>
          <button type='button' className='flex items-center justify-between p-8 bg-neutral-10 border border-neutral-20 rounded-8' onClick={toggleBilledMenu}>
            <div className='flex flex-col'>
              <p className='text-12 font-medium text-neutral-60 text-left'>Billed Entities</p>
              <div className='flex items-center gap-6'>
                <div className='w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center text-10 font-bold'>
                  {savedSelectedEntities.length}
                </div>
                <p
                  className='text-12 xl:text-14 font-medium truncate max-w-[150px] uppercase'
                  title={savedSelectedEntities.length > 0 ? savedSelectedEntities.map((entity) => entity.code).join(', ') : 'No entity selected'}
                >
                  {savedSelectedEntities.length > 0 ? savedSelectedEntities.map((entity) => beautifyEnum(entity.code)).join(', ') : 'No entity selected'}
                </p>
              </div>
            </div>
            <img src={IconSort} alt='sort' />
          </button>

          <ApiVersionSwitch />

          <DeleteModeSwitch />

          <div className='flex items-center justify-between p-8'>
            <button
              type='button'
              className='flex items-center gap-8'
              onClick={() => {
                // Clear all API cache before logout
                queryClient.clear();
                // Clear selected billed entities from Redux (which will also clear from local storage)
                dispatch(clearSelectedBilledEntities());
                // Logout the user
                dispatch(logoutActionCreator());
              }}
            >
              <img src={IconLogout} alt='logout' />
              <p className='text-12 xl:text-14 font-medium text-red-40'>Logout</p>
            </button>
          </div>
        </div>
      </div>

      {/* Billed Entities Menu */}
      <BilledEntitiesMenu isVisible={isShowBilledMenu} onClose={closeBilledMenu} onToggle={switchToAccountMenu} />
    </div>
  );
};
